import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentValue, setCurrentValue] = useState(0);

  const values = [
    "Innovation",
    "Excellence", 
    "Integrity",
    "Collaboration",
    "Impact"
  ];

  useEffect(() => {
    setIsVisible(true);
    
    const interval = setInterval(() => {
      setCurrentValue((prev) => (prev + 1) % values.length);
    }, 2500);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <SEO 
        title="About Us - Leading AI & Software Development Company"
        description="Learn about Abacies Logiciels - a global leader in AI solutions, custom software development, and digital transformation with 12+ years of excellence serving 100+ clients worldwide."
        keywords="about Abacies, company history, AI expertise, software development company, global technology partner"
      />

      {/* Enhanced Hero Section */}
      <section className="hero-section relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-red/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        
        <div className="page-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="inline-flex items-center px-4 py-2 bg-blush-light/50 rounded-full text-sm text-primary-red font-semibold mb-6">
                <span className="w-2 h-2 bg-primary-red rounded-full mr-2 animate-pulse"></span>
                Est. 2013 • Global Technology Leader
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal">
                Pioneering <span className="text-gradient">Innovation</span>
                <br />
                <span className="text-2xl md:text-3xl lg:text-4xl mt-2 inline-block">
                  Through{' '}
                  <span className="relative inline-block">
                    {values.map((value, index) => (
                      <span
                        key={index}
                        className={`absolute left-0 transition-all duration-500 ${
                          index === currentValue 
                            ? 'opacity-100 transform translate-y-0' 
                            : 'opacity-0 transform -translate-y-2'
                        }`}
                      >
                        <span className="text-gradient font-bold">{value}</span>
                      </span>
                    ))}
                    <span className="invisible">{values[0]}</span>
                  </span>
                </span>
              </h1>
              
              <p className="text-xl text-titanium leading-relaxed">
                Pioneering the future of enterprise technology with innovative AI solutions, custom software development, and digital transformation services since 2013.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview with Animations */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <h2 className="section-title">Our Story</h2>
              <div className="mb-4 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[1,2,3,4,5].map((i) => (
                    <div key={i} className="w-8 h-8 bg-gradient-to-br from-primary-red to-coral-mist rounded-full border-2 border-white"></div>
                  ))}
                </div>
                <span className="text-sm text-titanium">500+ Experts Worldwide</span>
              </div>
              
              <p className="text-titanium mb-6">
                Founded in 2013, Abacies Logiciels has grown from a visionary startup to a global leader in enterprise technology solutions. With a presence in 20 countries and a team of 500+ experts, we've helped transform businesses across industries through innovative AI applications and custom software development.
              </p>
              <p className="text-titanium mb-6">
                Our journey began with a simple mission: to make advanced technology accessible and practical for businesses of all sizes. Today, we're proud to serve Fortune 500 companies and innovative startups alike, delivering solutions that drive real business value.
              </p>
              
              {/* Achievement Badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                {['ISO 27001', 'SOC 2', 'GDPR', 'HIPAA'].map((cert, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-gradient-to-r from-blush-light to-transparent text-primary-red text-sm font-medium rounded-full border border-primary-red/20"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    ✓ {cert} Certified
                  </span>
                ))}
              </div>
              
              <Link to="/company/our-story" className="btn-primary inline-block group">
                Read Full Story
                <svg className="inline-block w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
            
            {/* Animated Stats Grid */}
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: '12+', label: 'Years of Excellence', icon: '📅', color: 'from-purple-500 to-pink-500' },
                { value: '100+', label: 'Global Clients', icon: '🌍', color: 'from-blue-500 to-cyan-500' },
                { value: '500+', label: 'Expert Team', icon: '👥', color: 'from-green-500 to-teal-500' },
                { value: '20', label: 'Countries', icon: '🗺️', color: 'from-orange-500 to-red-500' }
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="relative group"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} opacity-10 rounded-xl blur-xl group-hover:opacity-20 transition-opacity`}></div>
                  <div className="relative card text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                    <span className="text-3xl mb-3 block animate-bounce-slow" style={{ animationDelay: `${index * 100}ms` }}>
                      {stat.icon}
                    </span>
                    <div className="text-3xl font-bold text-primary-red mb-2 count-up">{stat.value}</div>
                    <div className="text-titanium">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision with Parallax Effect */}
      <section className="py-16 md:py-20 bg-alabaster relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-64 h-64 bg-coral-mist/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-red/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="page-container relative z-10">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="card group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-red to-coral-mist rounded-xl flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform">
                  🎯
                </div>
                <h3 className="text-2xl font-bold ml-4 text-charcoal">Our Mission</h3>
              </div>
              <p className="text-titanium">
                To empower businesses worldwide with cutting-edge AI solutions and custom software that drives innovation, efficiency, and growth. We strive to be the trusted technology partner that transforms challenges into opportunities through intelligent automation and digital excellence.
              </p>
              <div className="mt-4 flex items-center text-primary-red font-medium">
                <span>Driving Innovation Daily</span>
                <svg className="w-4 h-4 ml-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H6a1 1 0 100 2h4.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            <div className="card group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-coral-mist to-primary-red rounded-xl flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform">
                  🚀
                </div>
                <h3 className="text-2xl font-bold ml-4 text-charcoal">Our Vision</h3>
              </div>
              <p className="text-titanium">
                To be the global leader in enterprise AI and software solutions, recognized for our innovation, quality, and commitment to client success. We envision a future where every business can leverage the power of artificial intelligence to achieve unprecedented growth and efficiency.
              </p>
              <div className="mt-4 flex items-center text-primary-red font-medium">
                <span>Shaping Tomorrow's Technology</span>
                <svg className="w-4 h-4 ml-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H6a1 1 0 100 2h4.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values with Interactive Cards */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <h2 className="section-title text-center">Our Core Values</h2>
          <p className="section-subtitle text-center max-w-3xl mx-auto mb-12">
            The principles that guide our work and define our culture
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: 'Innovation', 
                description: 'Continuously pushing boundaries to deliver cutting-edge solutions that transform businesses',
                icon: '💡',
                color: 'from-purple-500 to-indigo-500'
              },
              { 
                title: 'Excellence', 
                description: 'Committed to delivering the highest quality in every project, exceeding expectations',
                icon: '⭐',
                color: 'from-yellow-500 to-orange-500'
              },
              { 
                title: 'Integrity', 
                description: 'Building trust through transparency, honesty, and ethical business practices',
                icon: '🤝',
                color: 'from-green-500 to-emerald-500'
              },
              { 
                title: 'Collaboration', 
                description: 'Working as partners with our clients to achieve shared success and growth',
                icon: '🤝',
                color: 'from-blue-500 to-cyan-500'
              },
              { 
                title: 'Agility', 
                description: 'Adapting quickly to changing needs and embracing new technologies',
                icon: '⚡',
                color: 'from-pink-500 to-rose-500'
              },
              { 
                title: 'Impact', 
                description: 'Creating meaningful change that drives real business value and societal benefit',
                icon: '🎯',
                color: 'from-red-500 to-orange-500'
              }
            ].map((value, index) => (
              <div 
                key={index} 
                className="relative group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${value.color} opacity-0 group-hover:opacity-10 rounded-xl blur-xl transition-opacity duration-300`}></div>
                <div className="relative text-center p-6 bg-white rounded-xl border border-platinum hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <span className="text-4xl mb-4 block animate-float" style={{ animationDelay: `${index * 200}ms` }}>
                    {value.icon}
                  </span>
                  <h3 className="text-xl font-semibold mb-3 text-charcoal">{value.title}</h3>
                  <p className="text-titanium">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence Animation */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-red to-deep-garnet text-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Global Reach, Local Impact
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              Serving clients across 20 countries with dedicated teams and 24/7 support
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { region: 'North America', offices: 5, clients: '40+' },
              { region: 'Europe', offices: 4, clients: '30+' },
              { region: 'Asia Pacific', offices: 6, clients: '25+' },
              { region: 'Middle East', offices: 3, clients: '15+' }
            ].map((region, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all duration-300 hover:scale-105"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <h3 className="text-xl font-bold mb-2">{region.region}</h3>
                <div className="flex justify-around mt-4">
                  <div>
                    <div className="text-2xl font-bold">{region.offices}</div>
                    <div className="text-sm opacity-80">Offices</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{region.clients}</div>
                    <div className="text-sm opacity-80">Clients</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team Preview with Hover Effects */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Leadership Team</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Visionary leaders with decades of combined experience in technology and business transformation
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { name: 'John Smith', role: 'Chief Executive Officer', expertise: 'Strategy & Vision' },
              { name: 'Sarah Johnson', role: 'Chief Technology Officer', expertise: 'AI & Innovation' },
              { name: 'Michael Chen', role: 'Chief Innovation Officer', expertise: 'Product Development' },
              { name: 'Emily Rodriguez', role: 'Chief Operating Officer', expertise: 'Operations Excellence' }
            ].map((leader, index) => (
              <div 
                key={index} 
                className="card text-center group hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative mb-4">
                  <div className="w-24 h-24 bg-gradient-to-br from-primary-red to-coral-mist rounded-full mx-auto group-hover:scale-110 transition-transform"></div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-primary-red text-white text-xs rounded-full">
                    {leader.expertise}
                  </div>
                </div>
                <h3 className="font-semibold text-charcoal">{leader.name}</h3>
                <p className="text-sm text-titanium">{leader.role}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/company/leadership" className="btn-secondary">
              Meet Full Team
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section with Animation */}
      <section className="py-16 md:py-20 bg-primary-red text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }}></div>
        </div>
        
        <div className="page-container text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Join Us in Shaping the Future
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Whether you're looking to transform your business or build your career, we're here to help you succeed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 hover:scale-105">
              Start a Project
            </Link>
            <Link to="/company/careers" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Join Our Team
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
